import React = require("react");
import {Popup} from "../../equipment/details/dashboard/lavazza/components/popup/Popup";
import {PopupController} from "../../equipment/details/dashboard/lavazza/components/popup/PopupController";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {
    dummySubmitFormAnswers,
    getFeedbackForm,
    getFeedbackForms,
    isValidFormAnswers,
    submitFormAnswers,
} from "../FeebackService";
import {Form, FormAnswers, uuid} from "../FeedbackFormModel";
import FeedbackModalController from "../FeedbackModalController";
import {FormBuilder} from "./FormBuilder";

import "./modal-styles.scss";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {Button} from "@vebox/etui-core";
import {BUTTON_STYLE} from "../../components/common/etui_components_styles";
import {veboxDefaultTheme} from "../../index";
import {VeBoxModal} from "../../components/modal/VeBoxModal";
import {Display} from "../../components/Display";
import {LoadingButton} from "../../components/buttons/LoadingButton";

declare var AppTranslations: AppTranslations;

interface OpenMethodParams {
    formTypeGuid: uuid;
    entityGuid?: uuid;
    entitiesGuid?: uuid[];
    onSubmit?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

interface Props {
    controller?: FeedbackModalController;
}

interface State {
    form?: Form;
    forms?: Form[];
    currentFormIndex?: number;
    feedbackAnswers: FormAnswers;
    openMethodParams?: OpenMethodParams;
    isValidForm: boolean;
}

export class FeedbackModal extends React.Component<Props, State> {

    private readonly controller: PopupController;

    constructor(props) {
        super(props);
        this.state = {
            feedbackAnswers: {questions: []},
            isValidForm: false,
        };
        this.controller = new PopupController();
        if (this.props.controller) {
            this.props.controller.closeMethod = this.close;
            this.props.controller.openMethod = this.open;
        }
    }

    public render() {
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <Popup
                    className={"position-fixed fill-parent top-left z-index-10e6"}
                    controller={this.controller}
                />
            </ThemeProvider>
        );
    }

    public componentWillUnmount() {
        this.controller.dispose();
    }

    public open = (params: OpenMethodParams) => {

        const cancel = () => {
            this.controller.closePopup();
            if (params.onCancel) {
                params.onCancel();
            }
        };

        if (params.entityGuid) {
            getFeedbackForm(
                params.formTypeGuid,
                params.entityGuid,
            ).then((form) => {

                if (form.userGuid && params.onError) {
                    params.onError();
                    return;
                }

                const submitAnswers = () => {
                    if (isValidFormAnswers(form, this.state.feedbackAnswers)) {
                        submitFormAnswers(
                            this.state.openMethodParams.formTypeGuid,
                            this.state.openMethodParams.entityGuid,
                            this.state.feedbackAnswers,
                        ).then(() => {
                            this.controller.closePopup();
                            if (this.state.openMethodParams.onSubmit) {
                                this.state.openMethodParams.onSubmit();
                            }
                        }).catch(() => {
                            console.error("Error: something went wrong while submitting answers.");
                            this.close();
                        });
                    } else {
                        console.error("The form isn't valid");
                    }
                };

                this.setState({
                    feedbackAnswers: {questions: []},
                    openMethodParams: params,
                    form,
                }, () => {

                    this.controller.showPopup(() => (
                        <Modal
                            form={form}
                            onCancel={cancel}
                            onSubmitForm={submitAnswers}
                            canSubmit={this.state.isValidForm}
                            onFormAnswerChange={this.onFormAnswerChange}
                        />
                    ), cancel);
                });
            }).catch(() => {
                console.error("Unable to display the feedback form.");
                if (params.onError) {
                    params.onError();
                }
            });
        } else if (params.entitiesGuid) {

            getFeedbackForms(
                params.formTypeGuid,
                params.entitiesGuid,
            )
                .then((forms) => forms.filter((form) => !form.userGuid))
                .then(this.renderFormModal(params))
                .catch(() => {
                    console.error("Unable to display the feedback forms.");
                    if (params.onError) {
                        params.onError();
                    }
                });
        }
    }

    public close = () => {
        this.controller.closePopup();
    }

    private renderFormModal = (params: OpenMethodParams) => (forms: Form[]) => {
        const cancel = () => {
            this.controller.closePopup();
            if (params.onCancel) {
                params.onCancel();
            }
        };

        const submitAndMoveToNextOrEnd = (form: Form) => {
            if (isValidFormAnswers(form, this.state.feedbackAnswers)) {
                submitFormAnswers(
                    this.state.form.formTypeGuid,
                    this.state.form.entityGuid,
                    this.state.feedbackAnswers,
                ).then(() => {
                    if (this.state.currentFormIndex === forms.length - 1) { // Is last form
                        this.controller.closePopup();
                        if (this.state.openMethodParams.onSubmit) {
                            this.state.openMethodParams.onSubmit();
                        }
                    } else {
                        this.setState({
                            feedbackAnswers: {questions: []},
                            form: forms[this.state.currentFormIndex + 1],
                            isValidForm: false,
                            currentFormIndex: this.state.currentFormIndex + 1,
                        });
                    }
                }).catch(() => {
                    console.error("Error: something went wrong while submitting answers.");
                    if (this.state.currentFormIndex === forms.length - 1) {
                        this.close();
                    } else {
                        this.setState({
                            feedbackAnswers: {questions: []},
                            form: forms[this.state.currentFormIndex + 1],
                            isValidForm: false,
                            currentFormIndex: this.state.currentFormIndex + 1,
                        });
                    }
                });
            } else {
                console.error("The form isn't valid");
            }
        };

        this.setState({
            feedbackAnswers: {questions: []},
            openMethodParams: params,
            form: forms[0],
            forms,
            currentFormIndex: 0,
        }, () => {
            this.controller.showPopup(() => (
                <Modal
                    form={this.state.form}
                    onCancel={cancel}
                    onSubmitForm={submitAndMoveToNextOrEnd}
                    canSubmit={this.state.isValidForm}
                    onFormAnswerChange={this.onFormAnswerChange}
                    text={{
                        submitButtonText: this.state.currentFormIndex < forms.length - 1
                            ? AppTranslations.genericWords.next
                            : AppTranslations.genericWords.submit,
                        pagination: forms.length > 1
                            ? (this.state.currentFormIndex + 1) + "/" + forms.length
                            : undefined,
                    }}
                />
            ), cancel);
        });
    }

    private onFormAnswerChange = (answers: FormAnswers) => {
        this.setState({
            feedbackAnswers: answers,
            isValidForm: isValidFormAnswers(this.state.form, answers),
        });
    }
}

interface ModalTexts {
    submitButtonText?: string;
    cancelButtonText?: string;
    pagination?: string;
}

interface ModalProps {
    form: Form;
    onFormAnswerChange: (answers: FormAnswers) => void;
    canSubmit: boolean;
    onCancel: () => void;
    onSubmitForm: (form: Form) => void;
    text?: ModalTexts;
}

const Modal: React.FC<ModalProps> = ({form, onFormAnswerChange, onCancel, canSubmit, onSubmitForm, text}) => {

    const submitAnswers = () => {
        onSubmitForm(form);
    };

    return (
        <VeBoxModal key={form.entityGuid} style={{width: 700}}>
            <VeBoxModal.Header title={form.title} onClose={onCancel}>
                {/* How to view the pagination */}
                <Display isVisible={text !== undefined && text.pagination !== undefined}>
                    <h3>{(text || {}).pagination}</h3>
                </Display>
                {/*How to view the pagination*/}
            </VeBoxModal.Header>
            <VeBoxModal.Body style={{maxHeight: "calc(100vh - 151px)"}}>
                <VeBoxModal.Section>
                    {form.description}
                </VeBoxModal.Section>
                <Display isVisible={form.entityInfoList}>
                    <VeBoxModal.Section>
                        {form.entityInfoList.map((x) => (
                            <p key={x.value} className={"summary-row space-between"}>
                                <span className={"text-no-wrap"}>{x.label}:</span>
                                <span className={"info"}>{x.value}</span>
                            </p>
                        ))}
                    </VeBoxModal.Section>
                </Display>
                <VeBoxModal.Section>
                    <FormBuilder
                        className={"body"}
                        entityClassName={"form-row"}
                        questions={form.questions}
                        onChange={onFormAnswerChange}
                    />
                    <Display isVisible={form.questions.reduce((a, x) => x.required || a, false)}>
                        <div className={"red bottom-warning-text capitalize"}>
                            *{AppTranslations.feedbackModal.requiredWarning}
                        </div>
                    </Display>
                </VeBoxModal.Section>
            </VeBoxModal.Body>
            <VeBoxModal.Actions>
                <Button
                    onClick={onCancel}
                    styles={BUTTON_STYLE}
                    variant={"tertiary"}
                >
                    {(text || {}).cancelButtonText || AppTranslations.genericWords.cancel}
                </Button>
                <LoadingButton
                    onClick={submitAnswers}
                    variant={"strong"}
                    disabled={!canSubmit}
                    styles={BUTTON_STYLE}
                >
                    {(text || {}).submitButtonText || AppTranslations.genericWords.submit}
                </LoadingButton>
            </VeBoxModal.Actions>
        </VeBoxModal>
    );
};